
import { defineComponent } from 'vue';

import DangerIcon from '@/ui/components/bases/icons/DangerIcon.vue';
import UpdateIcon from '@/ui/components/bases/icons/UpdateIcon.vue';
import PalaLink from '../components/bases/PalaLink.vue';
import { cefRequest } from "../../utils/cef";
import { bench } from "../../utils/bench";
import * as Games from "../../domain/Games";
import { Game } from "../../typings/games";
import LottieAnimation from "lottie-vuejs/src/LottieAnimation.vue";

const BASE_DELAY_MS = 2000;
const MAX_DELAY_MS = 15000;
const MAX_BACKOFF_STEP = 10;

export default defineComponent({
  name: 'SplashPage',
  components: {
    PalaLink,
    DangerIcon,
    UpdateIcon,
    LottieAnimation,
  },
  data() {
    return {
      isUnderMaintenance: false,
      isUnderUpdate: false,
      attempt: 0,
      retryTimer: 0 as any,
      anim: null as any,
    };
  },
  mounted() {
    this.boot();
  },
  beforeUnmount() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
    if (this.anim) {
      this.anim.destroy();
    }
  },
  methods: {
    onAnim(anim: any) {
      this.anim = anim;
      const elements: HTMLCollection = document.getElementsByTagName('svg');
      for (let i = 0; i < elements.length; i++) {
        const e: any = elements[i];
        e.style.transform = '';
      }
    },
    async boot() {
      // The retry below is silent by design: the player must never perceive a connection incident
      // on the splash.
      const tStart = performance.now();
      try {
        let t = performance.now();
        const logged = await cefRequest("GETTER", "IS_LOGGED", {});
        bench('splash/IS_LOGGED', performance.now() - t);
        if (logged.status !== 200) {
          this.$router.push({ name: "Login" });
          return;
        }

        t = performance.now();
        const token = await cefRequest("GETTER", "GET_TOKEN", {});
        bench('splash/GET_TOKEN', performance.now() - t);
        if (token.status !== 200 || !token.payload || !token.payload.jwt) {
          throw new Error('Impossible de vérifier votre compte');
        }

        t = performance.now();
        // The first attempt may serve a cached list for speed; retries force a real re-fetch so the
        // silent retry actually re-queries the API instead of replaying the same cached store.
        const games: Game[] = this.attempt > 0
          ? await Games.refreshGames(token.payload.jwt)
          : await Games.getGames(token.payload.jwt);
        bench('splash/getGames', performance.now() - t);
        // Paladium is the only game: without it we stay on the splash and keep retrying silently
        // until it shows up, so the homepage never loads a fallback game.
        const paladium: Game | undefined = games.find((game: Game) => game.id === 'paladium');
        if (paladium === undefined) {
          throw new Error('Paladium indisponible');
        }

        bench('splash/TOTAL', performance.now() - tStart);
        this.attempt = 0;
        this.$router.push(paladium.id);
      } catch (error) {
        console.error(error);
        this.scheduleRetry();
      }
    },
    scheduleRetry() {
      this.attempt++;
      const step = Math.min(this.attempt - 1, MAX_BACKOFF_STEP);
      const delay = Math.min(BASE_DELAY_MS * Math.pow(2, step), MAX_DELAY_MS);
      // Silent retry: nothing on screen changes, no countdown, no alert.
      this.retryTimer = setTimeout(() => this.boot(), delay);
    },
  },
});
